import { useRef, useState } from "react";
import { buildVoiceAnnotatedHtml } from "@utils/mode_tool/pointEditorHelpers";

function VoiceAnnotatedTextEditor({
  value,
  voices = [],
  expressions = [],
  baseVoice = "",
  assignments = [],
  onChange,
  onAssign,
}) {
  const editorRef = useRef(null);
  const caretOffsetRef = useRef(null);
  const caretRangeRef = useRef(null);
  const [selection, setSelection] = useState(null);
  const [isExpressionMenuOpen, setIsExpressionMenuOpen] = useState(false);

  function getRangeOffsets() {
    const editor = editorRef.current;
    const browserSelection = window.getSelection();
    if (!editor || !browserSelection?.rangeCount) return null;
    const range = browserSelection.getRangeAt(0);
    if (range.collapsed || !editor.contains(range.commonAncestorContainer)) {
      return null;
    }
    const beforeRange = range.cloneRange();
    beforeRange.selectNodeContents(editor);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const source = editor.innerText || "";
    const rawStart = beforeRange.toString().length;
    const rawEnd = rawStart + range.toString().length;
    let start = rawStart;
    let end = rawEnd;
    const isWordCharacter = (character) => /[\p{L}\p{N}'’\-]/u.test(character || "");
    // TTS segments must never cut through a word. Expand partial selections to
    // their natural word boundaries before persisting the range.
    while (
      start > 0 &&
      isWordCharacter(source[start - 1]) &&
      isWordCharacter(source[start])
    ) {
      start -= 1;
    }
    while (
      end < source.length &&
      isWordCharacter(source[end - 1]) &&
      isWordCharacter(source[end])
    ) {
      end += 1;
    }
    const text = source.slice(start, end);
    if (!text.trim()) return null;
    const editorBounds = editor.getBoundingClientRect();
    let normalizedRange = range;
    if (start !== rawStart || end !== rawEnd) {
      const getTextPosition = (offset) => {
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
        let consumed = 0;
        let lastNode = null;
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const length = node.textContent?.length || 0;
          if (offset <= consumed + length) {
            return { node, offset: Math.max(0, offset - consumed) };
          }
          consumed += length;
          lastNode = node;
        }
        return lastNode
          ? { node: lastNode, offset: lastNode.textContent?.length || 0 }
          : null;
      };
      const startPosition = getTextPosition(start);
      const endPosition = getTextPosition(end);
      if (startPosition && endPosition) {
        normalizedRange = document.createRange();
        normalizedRange.setStart(startPosition.node, startPosition.offset);
        normalizedRange.setEnd(endPosition.node, endPosition.offset);
        browserSelection.removeAllRanges();
        browserSelection.addRange(normalizedRange);
      }
    }
    const rangeBounds = normalizedRange.getBoundingClientRect();
    return {
      start,
      end,
      text,
      left: Math.max(8, rangeBounds.left - editorBounds.left),
      top: Math.max(8, rangeBounds.bottom - editorBounds.top + 6),
    };
  }

  function getCaretOffsetFromRange(range) {
    const editor = editorRef.current;
    if (
      !editor ||
      !range ||
      !range.collapsed ||
      !editor.contains(range.commonAncestorContainer)
    ) {
      return null;
    }
    const beforeRange = range.cloneRange();
    beforeRange.selectNodeContents(editor);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    return beforeRange.toString().length;
  }

  function getCaretOffset() {
    const browserSelection = window.getSelection();
    if (!browserSelection?.rangeCount) return null;
    return getCaretOffsetFromRange(browserSelection.getRangeAt(0));
  }

  function rememberCaretOffset() {
    const browserSelection = window.getSelection();
    if (!browserSelection?.rangeCount) return;
    const range = browserSelection.getRangeAt(0);
    const caretOffset = getCaretOffsetFromRange(range);
    if (Number.isFinite(caretOffset)) {
      caretOffsetRef.current = caretOffset;
      caretRangeRef.current = range.cloneRange();
    }
  }

  function rememberCaretFromPoint(clientX, clientY) {
    const editor = editorRef.current;
    if (!editor) return;
    let range = null;

    if (typeof document.caretPositionFromPoint === "function") {
      const position = document.caretPositionFromPoint(clientX, clientY);
      if (position?.offsetNode) {
        range = document.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
      }
    } else if (typeof document.caretRangeFromPoint === "function") {
      range = document.caretRangeFromPoint(clientX, clientY);
    }

    const caretOffset = getCaretOffsetFromRange(range);
    if (!Number.isFinite(caretOffset)) return;
    caretOffsetRef.current = caretOffset;
    caretRangeRef.current = range.cloneRange();
  }

  function restoreCaretOffset(offset) {
    const editor = editorRef.current;
    if (!editor || !Number.isFinite(offset)) return;
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let consumed = 0;
    let targetNode = null;
    let targetOffset = 0;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const length = node.textContent?.length || 0;
      if (offset <= consumed + length) {
        targetNode = node;
        targetOffset = Math.max(0, offset - consumed);
        break;
      }
      consumed += length;
      targetNode = node;
      targetOffset = length;
    }
    if (!targetNode) return;
    const range = document.createRange();
    range.setStart(targetNode, targetOffset);
    range.collapse(true);
    const browserSelection = window.getSelection();
    browserSelection?.removeAllRanges();
    browserSelection?.addRange(range);
  }

  function captureSelection() {
    const nextSelection = getRangeOffsets();
    if (!nextSelection) {
      setSelection(null);
      return;
    }
    setSelection(nextSelection);
  }

  function scheduleSelectionCapture() {
    // Native text selection is finalized just after mouse/pointer release.
    // A short timeout makes this reliable across browsers and marked spans.
    window.setTimeout(captureSelection, 0);
  }

  function assignSelectedText(voice) {
    if (!selection?.text || !voice) return;
    onAssign({ ...selection, voice });
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  }

  function insertExpression(expression) {
    const tag = String(expression || "").trim();
    if (!tag) return;
    const savedCaretOffset = getCaretOffsetFromRange(caretRangeRef.current);
    const insertionOffset = Math.max(
      0,
      Math.min(
        value.length,
        Number.isFinite(caretOffsetRef.current)
          ? caretOffsetRef.current
          : Number.isFinite(savedCaretOffset)
            ? savedCaretOffset
            : value.length,
      ),
    );
    const needsLeadingSpace =
      insertionOffset > 0 && !/\s/.test(value[insertionOffset - 1] || "");
    const needsTrailingSpace =
      insertionOffset < value.length && !/\s/.test(value[insertionOffset] || "");
    const insertedText = `${needsLeadingSpace ? " " : ""}${tag}${
      needsTrailingSpace ? " " : ""
    }`;
    onChange(
      `${value.slice(0, insertionOffset)}${insertedText}${value.slice(insertionOffset)}`,
    );
    setSelection(null);
    setIsExpressionMenuOpen(false);
    window.requestAnimationFrame(() => {
      restoreCaretOffset(insertionOffset + insertedText.length);
      caretOffsetRef.current = insertionOffset + insertedText.length;
      caretRangeRef.current = null;
      editorRef.current?.focus();
    });
  }

  return (
    <div className="mode-route-description-ai-annotated-editor">
      {expressions.length ? (
        <div className="mode-route-description-ai-expression-toolbar">
          <button
            type="button"
            className="mode-route-description-ai-expression-trigger"
            aria-expanded={isExpressionMenuOpen}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setIsExpressionMenuOpen((isOpen) => !isOpen)}
          >
            + Expression
          </button>
          {isExpressionMenuOpen ? (
            <div className="mode-route-description-ai-expression-menu">
              {expressions.map((expression) => (
                <button
                  key={expression}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertExpression(expression)}
                  title={`Insert ${expression}`}
                >
                  {expression}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        ref={editorRef}
        className="field-input mode-route-description-ai-generated-input"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder="Write the audio description here"
        dangerouslySetInnerHTML={{
          __html: buildVoiceAnnotatedHtml({
            value,
            voices,
            baseVoice,
            assignments,
          }),
        }}
        onInput={(event) => {
          const caretOffset = getCaretOffset();
          if (Number.isFinite(caretOffset)) {
            caretOffsetRef.current = caretOffset;
          }
          onChange((event.currentTarget.innerText || "").replace(/\r/g, ""));
          if (caretOffset !== null) {
            window.requestAnimationFrame(() => restoreCaretOffset(caretOffset));
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          document.execCommand("insertText", false, "\n");
        }}
        onPointerDown={(event) => {
          rememberCaretFromPoint(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          rememberCaretFromPoint(event.clientX, event.clientY);
          scheduleSelectionCapture();
        }}
        onMouseUp={(event) => {
          rememberCaretFromPoint(event.clientX, event.clientY);
          scheduleSelectionCapture();
        }}
        onClick={(event) => {
          rememberCaretFromPoint(event.clientX, event.clientY);
        }}
        onSelect={scheduleSelectionCapture}
        onKeyUp={() => {
          captureSelection();
          rememberCaretOffset();
        }}
      />
      {selection && voices.length > 1 ? (
        <div
          className="mode-route-description-ai-selection-actions mode-route-description-ai-selection-popover"
          style={{ left: selection.left, top: selection.top }}
          role="status"
        >
          <span className="mode-route-description-ai-selection-summary">
            Assign selected text
          </span>
          <div className="mode-route-description-ai-selection-buttons">
            {voices.slice(0, 2).map((voice, index) => (
              <button
                key={voice.id}
                type="button"
                className={`mode-route-description-ai-speaker-action ${
                  index === 1 ? "is-speaker-two" : "is-speaker-one"
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => assignSelectedText(voice.id)}
              >
                <span>
                  Speaker {index + 1}{voice.gender ? ` · ${voice.gender}` : ""}
                </span>
                {voice.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VoiceAnnotatedTextEditor;
